import { useCallback, useEffect, useState } from 'react';
import { refreshTokenApi } from '../api/auth';
import { useAuthStore } from '../store/authStore';
import {
  coordinatedTokenRefresh,
  tryRestorePublishedSession,
} from '../utils/authSessionCoordinator';
import {
  invalidateAuthSession,
  isSilentRefreshBlocked,
} from '../utils/invalidateAuthSession';
import { resolveCsrfToken } from '../utils/csrf';

export function useSessionBootstrap(): { ready: boolean } {
  const token = useAuthStore((s) => s.token);
  const csrfToken = useAuthStore((s) => s.csrfToken);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const [refreshDone, setRefreshDone] = useState(false);

  const markDone = useCallback(() => setRefreshDone(true), []);

  const syncReady = hasHydrated && token !== null;

  useEffect(() => {
    if (syncReady) return;
    if (!hasHydrated) return;

    if (tryRestorePublishedSession()) {
      markDone();
      return;
    }

    if (isSilentRefreshBlocked()) {
      markDone();
      return;
    }

    const csrf = resolveCsrfToken(csrfToken);
    if (!csrf) {
      markDone();
      return;
    }

    const csrfForRefresh = csrf;
    let cancelled = false;

    async function restore() {
      const result = await coordinatedTokenRefresh(() =>
        refreshTokenApi({ csrfToken: csrfForRefresh, accessToken: null })
      );

      if (cancelled) {
        return;
      }

      if (!result.success) {
        await invalidateAuthSession();
      }

      markDone();
    }

    void restore();

    return () => {
      cancelled = true;
    };
  }, [syncReady, hasHydrated, csrfToken, markDone]);

  const ready = syncReady || refreshDone;

  return { ready };
}
